import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'; //-tech-debt-react-query;
import { deleteItem, fetchItem, fetchItems, postItem, putItem } from '../support/queries';
import { useFetchItem, useFetchItems } from './base-hooks';
import { DEFAULT_QUERY_OPTIONS } from '../support/constants';
import { CacheKey } from '../types/cache-keys';
import { AIQuestion } from '../types/aiTypes';
import {
  Campaign,
  CampaignActionItem,
  CampaignAsset,
  CampaignCapability,
  CampaignCapabilityLink,
  CampaignContentBlock,
  CampaignCustomerPersona,
  CampaignCustomerPersonaLink,
  CampaignDelivery,
  CampaignDeliveryStepStatus,
  CampaignDeliveryTarget,
  CampaignEnrollment,
  CampaignGroupAssignment,
  CampaignOutcome,
  CampaignOutcomeLink,
  CampaignPhase,
  CampaignPrompt,
  CampaignQuestion,
  CampaignQuestionLink,
  CampaignReportFigure,
  CampaignReportSection,
  CampaignStep,
  CampaignWorkshopMeeting,
  CampaignWorkshopTeamRole,
  CampaignWorkshopTeamRoleLink,
} from '../support/campaigns';
import type { CampaignCompletionResponse, SalesLevelUser } from '../support/campaignSalesLevels';
import type { AdaptiveCard } from '../support/campaignEnablementNotify';
import type { Order } from '../types/taskManager';
import { store } from '../support/store';
import { isTerminalQueueOrder, useQueueOrderStatus } from './queue-order-hooks';

/**
 * Data hooks for the modular Campaign feature.
 *
 * All seven tables are plain generic CRUD in toolbox_db, so reads go through the shared
 * `useFetchItem(s)` helpers and writes through `postItem`/`putItem`/`deleteItem` — `putItem` rather
 * than `patchItem` because toolbox_db's Go runtime serves PUT and 405s on PATCH. That PUT is a
 * *full* update: it validates like a create, so every write must carry the whole record (all
 * required fields) with the change applied on top, not just the changed field, or it 400s.
 *
 * A campaign's step tree is fetched flat by `?campaign=<id>` and assembled client-side (see
 * `buildStepTree` in support/campaigns.ts); the delivery tables are fetched by `?campaign=<id>` /
 * `?delivery=<id>` the same way. Both the resource surface and the admin surface share these.
 */

const ENDPOINTS = {
  campaign: 'db/campaign',
  step: 'db/campaign-step',
  enrollment: 'db/campaign-enrollment',
  delivery: 'db/campaign-delivery',
  target: 'db/campaign-delivery-target',
  stepStatus: 'db/campaign-delivery-step-status',
  actionItem: 'db/campaign-action-item',
  workshopMeeting: 'db/campaign-workshop-meeting',
  personas: 'db/campaign-customer-persona',
  teamRoles: 'db/campaign-workshop-team-role',
  reportSections: 'db/campaign-report-section',
  reportFigures: 'db/campaign-report-figure',
  outcomes: 'db/campaign-outcome',
  questions: 'db/campaign-question',
  capabilities: 'db/campaign-capability',
  // Reference-object membership join tables (globalization, 2026-09-25). Each links a campaign to a
  // subset of the (now global) reference objects above.
  personaLinks: 'db/campaign-customer-persona-link',
  teamRoleLinks: 'db/campaign-workshop-team-role-link',
  outcomeLinks: 'db/campaign-outcome-link',
  questionLinks: 'db/campaign-question-link',
  capabilityLinks: 'db/campaign-capability-link',
  contentBlocks: 'db/campaign-content-block',
  assets: 'db/campaign-asset',
  phases: 'db/campaign-phase',
  prompts: 'db/campaign-prompt',
  groupAssignments: 'db/campaign-group-assignment',
  userGroups: 'db/user-group',
} as const;

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Campaigns, filtered server-side. Pass `{ status: 'published' }` for the resource catalogue. */
export const useFetchCampaigns = (params?: Record<string, any>, options?: object) =>
  useFetchItems<Campaign[]>(
    ENDPOINTS.campaign,
    () => [CacheKey.Campaigns, JSON.stringify(params ?? {})],
    params,
    options
  );

export const useFetchCampaign = (id?: number | string, options?: object) =>
  useFetchItem<Campaign>(ENDPOINTS.campaign, id, CacheKey.Campaign, undefined, options);

export const useFetchCampaignSteps = (campaignId?: number | string, options?: object) =>
  useFetchItems<CampaignStep[]>(
    ENDPOINTS.step,
    () => [CacheKey.CampaignStep, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );

export const useFetchEnrollments = (campaignId?: number | string, options?: object) =>
  useFetchItems<CampaignEnrollment[]>(
    ENDPOINTS.enrollment,
    () => [CacheKey.CampaignEnrollment, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );

/**
 * Every campaign enrollment, unfiltered — the source the Leader "Campaigns" tab aggregates by person
 * (see `enrollmentsByUser`) over an org subtree for the enablement / "one-time work" rollup. There is
 * no owner-scoped server filter, so the whole (small) table is pulled and scoped client-side, exactly
 * like `useFetchAllDeliveries`. Distinct cache key from the per-campaign `useFetchEnrollments` list.
 */
export const useFetchAllEnrollments = (options?: object) =>
  useFetchItems<CampaignEnrollment[]>(
    ENDPOINTS.enrollment,
    () => [CacheKey.CampaignEnrollment, 'all'],
    undefined,
    options
  );

export const useFetchDeliveries = (campaignId?: number | string, options?: object) =>
  useFetchItems<CampaignDelivery[]>(
    ENDPOINTS.delivery,
    () => [CacheKey.CampaignDelivery, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );

/**
 * Every campaign delivery, unfiltered — the source the Leader "Campaigns" tab aggregates by `owner`
 * over an org subtree (there is no owner-scoped server filter, and set-membership across a subtree is
 * a client-side concern). Distinct cache key from the per-campaign `useFetchDeliveries` list. Small
 * table; fine to pull whole. Callers scope by `owner`/`campaign` client-side.
 */
export const useFetchAllDeliveries = (options?: object) =>
  useFetchItems<CampaignDelivery[]>(
    ENDPOINTS.delivery,
    () => [CacheKey.CampaignDelivery, 'all'],
    undefined,
    options
  );

export const useFetchDeliveryTargets = (deliveryId?: number | string, options?: object) =>
  useFetchItems<CampaignDeliveryTarget[]>(
    ENDPOINTS.target,
    () => [CacheKey.CampaignDeliveryTarget, deliveryId ?? 'none'],
    deliveryId ? { delivery: deliveryId } : undefined,
    { enabled: !!deliveryId, ...(options || {}) }
  );

/**
 * Every delivery target, unfiltered — the Leader "Campaigns" tab joins these to deliveries
 * (`target.delivery`) to reach each delivery's linked Salesforce opportunity (`target.opportunity`)
 * for the pipeline-$ rollup. Same "small table, pull whole, scope client-side" rationale as
 * `useFetchAllDeliveries`; distinct cache key from the per-delivery `useFetchDeliveryTargets`.
 */
export const useFetchAllDeliveryTargets = (options?: object) =>
  useFetchItems<CampaignDeliveryTarget[]>(
    ENDPOINTS.target,
    () => [CacheKey.CampaignDeliveryTarget, 'all'],
    undefined,
    options
  );

export const useFetchDeliveryStepStatuses = (deliveryId?: number | string, options?: object) =>
  useFetchItems<CampaignDeliveryStepStatus[]>(
    ENDPOINTS.stepStatus,
    () => [CacheKey.CampaignDeliveryStepStatus, deliveryId ?? 'none'],
    deliveryId ? { delivery: deliveryId } : undefined,
    { enabled: !!deliveryId, ...(options || {}) }
  );

/**
 * The whole campaign's delivery step-status rows in ONE request, via the Go `toolbox_db` bespoke read
 * route `GET db/campaign/{id}/analytics/`. This collapses the admin analytics dashboard's per-delivery
 * fan-out — one `?delivery=<id>` request each, N per open campaign — into a single server-side join.
 *
 * The route is a Go-only optimization with no Django counterpart: dev and uat run the Go runtime and
 * serve it, but prod is still Django (404), and a dev/uat runtime mid-rollout may not have it yet. So
 * `retry: false` makes a miss fail fast and the caller (`CampaignView`) falls back to the fan-out on
 * `isError`. The response is a drop-in for the array the fan-out assembles (same row shape, same field
 * names), so nothing downstream changes whichever path serves it. Distinct cache key (`'rollup'`
 * segment) from the per-delivery `useFetchDeliveryStepStatuses` list so the two never collide.
 */
export const useCampaignStepStatusRollup = (campaignId?: number | string, options?: object) =>
  useFetchItems<CampaignDeliveryStepStatus[]>(
    campaignId ? `${ENDPOINTS.campaign}/${campaignId}/analytics` : ENDPOINTS.campaign,
    () => [CacheKey.CampaignDeliveryStepStatus, campaignId ?? 'none', 'rollup'],
    undefined,
    { enabled: !!campaignId, retry: false, ...(options || {}) }
  );

/**
 * Server-aggregated enablement completion for a campaign, via the Go `toolbox_db` bespoke read route
 * `GET db/campaign/{id}/completion/`. It classifies every enrollment by the same composite-aware rollup
 * the per-row `progress` field uses and returns pre-grouped cohorts (per territory tuple, or per owner
 * email), so the Admin dashboard and Leader team pages scale past the generic list API's page cap
 * (~10k enrollments) — the frontend never fetches the raw rows.
 *
 * `by`: `'territory'` (default; whole-org aggregate, served to any authenticated caller) or `'owner'`
 * (per-person, scoped server-side to the caller's own subtree unless they hold global.proxy /
 * is_global_leader). `manager`: an email to root the recursive subtree at — the server 403s if it is
 * outside the caller's own subtree and they are not privileged. Visibility is enforced server-side; the
 * client cannot widen it.
 *
 * Go-only optimization with no Django counterpart, so `retry: false` fails fast on a runtime that lacks
 * it and the caller falls back to the client-side enrollment rollup. Canonicalisation (Global/Unassigned
 * collapse, drill grouping) stays on the client in `campaignSalesLevels.ts`.
 */
export const useCampaignCompletion = (
  campaignId?: number | string,
  opts: { by?: 'territory' | 'owner'; manager?: string } = {},
  options?: { enabled?: boolean } & Record<string, unknown>,
): UseQueryResult<CampaignCompletionResponse, unknown> => {
  const by = opts.by ?? 'territory';
  const params: Record<string, string> = { by };
  if (opts.manager) params.manager = opts.manager;

  const enabled = !!campaignId && (options?.enabled ?? true);

  return useQuery({
    queryKey: [String(CacheKey.CampaignEnrollment), String(campaignId ?? 'none'), 'completion', by, opts.manager ?? ''],
    queryFn: () => fetchItems(`${ENDPOINTS.campaign}/${campaignId}/completion`, params),
    ...{ ...DEFAULT_QUERY_OPTIONS, retry: false, ...(options || {}), enabled },
  });
};

export const useFetchActionItems = (deliveryId?: number | string, options?: object) =>
  useFetchItems<CampaignActionItem[]>(
    ENDPOINTS.actionItem,
    () => [CacheKey.CampaignActionItem, deliveryId ?? 'none'],
    deliveryId ? { delivery: deliveryId } : undefined,
    { enabled: !!deliveryId, ...(options || {}) }
  );

export const useFetchWorkshopMeetings = (deliveryId?: number | string, options?: object) =>
  useFetchItems<CampaignWorkshopMeeting[]>(
    ENDPOINTS.workshopMeeting,
    () => [CacheKey.CampaignWorkshopMeeting, deliveryId ?? 'none'],
    deliveryId ? { delivery: deliveryId } : undefined,
    { enabled: !!deliveryId, ...(options || {}) }
  );

/**
 * Every workshop meeting, unfiltered — the analytics pages derive each delivery's schedule status
 * (Not Scheduled / Scheduled / In-Progress / Complete) from these rows' UTC start/end (see
 * `campaignWorkshopStatus`). Small table, pulled whole and scoped client-side by delivery/campaign,
 * exactly like `useFetchAllDeliveries`. Distinct cache key from the per-delivery list.
 */
export const useFetchAllWorkshopMeetings = (options?: object) =>
  useFetchItems<CampaignWorkshopMeeting[]>(
    ENDPOINTS.workshopMeeting,
    () => [CacheKey.CampaignWorkshopMeeting, 'all'],
    undefined,
    options
  );

/**
 * Every user's Cisco sales territory (`sales_level_1`/`sales_level_2`), from the generic `db/user`
 * list — the join source that lets the reporting pages group enrollments (by `user_email`/`user`) and
 * deliveries (by owner email, until the `owner_user` backfill lands) by sales level. `db/user`
 * serializes all fields, so the territory columns come back for every user. `fetchItems` defaults to
 * `includePageSize:true`, so this returns the whole table rather than the 10-row default trap.
 */
export const useFetchUserSalesLevels = (options?: object) =>
  useFetchItems<SalesLevelUser[]>(
    'db/user',
    () => [CacheKey.User, 'sales-levels'],
    undefined,
    { staleTime: 5 * 60 * 1000, ...(options || {}) }
  );

/**
 * Reference-object globalization (2026-09-25). The five campaign reference objects (personas, team
 * roles, outcomes, questions, capabilities) are now global rows; a campaign references a subset of
 * each through a per-type join table (`campaign_<type>_link`). This helper resolves that subset for a
 * campaign: it fetches the campaign's link rows AND the (small, ≤1000-row) global object table, then
 * returns just the linked objects, sorted by `order`. The returned object mimics a react-query result
 * (`data`, `isLoading`, `isFetching`, `error`, `refetch`) so existing consumers keep reading `.data`;
 * `links` is exposed too so editors can drive attach/detach. `data` is the raw linked subset (hidden
 * rows included) — the admin builder sees them; consumers filter `hidden` themselves.
 */
const useLinkedReferenceObjects = <TObj extends { id: number; order?: number }>(config: {
  campaignId?: number | string;
  objectEndpoint: string;
  objectCacheKey: CacheKey;
  linkEndpoint: string;
  linkCacheKey: CacheKey;
  /** The link row's FK field pointing at the object, e.g. 'persona' / 'outcome' / 'capability'. */
  linkObjectField: string;
  options?: object;
}) => {
  const {
    campaignId,
    objectEndpoint,
    objectCacheKey,
    linkEndpoint,
    linkCacheKey,
    linkObjectField,
    options,
  } = config;

  const linksQuery = useFetchItems<Array<Record<string, number>>>(
    linkEndpoint,
    () => [linkCacheKey, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );
  const objectsQuery = useFetchItems<TObj[]>(
    objectEndpoint,
    () => [objectCacheKey, 'all'],
    undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );

  const links = linksQuery.data ?? [];
  const linkedIds = new Set(links.map((l) => l[linkObjectField]));
  const data = (objectsQuery.data ?? [])
    .filter((o) => linkedIds.has(o.id))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return {
    data,
    links,
    isLoading: linksQuery.isLoading || objectsQuery.isLoading,
    isFetching: linksQuery.isFetching || objectsQuery.isFetching,
    error: linksQuery.error || objectsQuery.error,
    refetch: async () => {
      await Promise.all([linksQuery.refetch(), objectsQuery.refetch()]);
    },
  };
};

/**
 * The campaign's customer personas (the workshop's "who to get in the room" reference) — the personas
 * this campaign LINKS (see `useLinkedReferenceObjects`; personas are global). `hidden` rows are
 * retained for admin add/remove retention but dropped in `.personas` so the runner view only sees
 * live ones. Replaces the panel's static STAKEHOLDERS.
 */
export const useCustomerPersonas = (campaignId?: number | string, options?: object) => {
  const query = useLinkedReferenceObjects<CampaignCustomerPersona>({
    campaignId,
    objectEndpoint: ENDPOINTS.personas,
    objectCacheKey: CacheKey.CampaignCustomerPersona,
    linkEndpoint: ENDPOINTS.personaLinks,
    linkCacheKey: CacheKey.CampaignCustomerPersonaLink,
    linkObjectField: 'persona',
    options,
  });
  return {
    ...query,
    personas: query.data.filter((p) => !p.hidden),
  };
};

/**
 * The campaign's Cisco-side workshop team roles — the roles this campaign LINKS (roles are global).
 * `hidden` rows are retained but dropped in `.roles`. Replaces the panel's static TEAM_ROLES literal.
 */
export const useWorkshopTeamRoles = (campaignId?: number | string, options?: object) => {
  const query = useLinkedReferenceObjects<CampaignWorkshopTeamRole>({
    campaignId,
    objectEndpoint: ENDPOINTS.teamRoles,
    objectCacheKey: CacheKey.CampaignWorkshopTeamRole,
    linkEndpoint: ENDPOINTS.teamRoleLinks,
    linkCacheKey: CacheKey.CampaignWorkshopTeamRoleLink,
    linkObjectField: 'role',
    options,
  });
  return {
    ...query,
    roles: query.data.filter((r) => !r.hidden),
  };
};

/**
 * The campaign's report-section config, filtered server-side by campaign and ordered by `order`.
 * This is the data-driven replacement for the frontier runner's hard-coded report constants: the
 * ordered section list, the data-section marker, the default-hidden set, and the section->ai_question
 * bindings. Empty (un-seeded campaign) → the runner falls back to its bundled constants, so the
 * standalone demo and campaigns without a report template still render.
 */
export const useReportSectionConfig = (campaignId?: number | string, options?: object) => {
  return useFetchItems<CampaignReportSection[]>(
    ENDPOINTS.reportSections,
    () => [CacheKey.CampaignReportSection, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );
};

/**
 * The campaign's report *figures* — the charts and images an admin arranges on the shared report
 * canvas — filtered server-side by campaign and ordered by `order`. Empty (no authored figures) →
 * the runner falls back to the workshop's own `state.reportLayout`, so the standalone demo and
 * campaigns without an authored canvas still render their figures. Per-delivery data fills each
 * authored slot at render time (see `useReportFigures`).
 */
export const useReportFigureConfig = (campaignId?: number | string, options?: object) => {
  return useFetchItems<CampaignReportFigure[]>(
    ENDPOINTS.reportFigures,
    () => [CacheKey.CampaignReportFigure, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );
};

/**
 * The campaign's outcomes — the prioritized "what this motion is meant to achieve" list that steers AI
 * suggestions/context — filtered server-side by campaign and ordered by `order` (lowest = highest
 * priority). Returns the raw config (admin sees hidden rows too); consumers filter `hidden` for
 * generation. The customer/internal `context` split is preserved for downstream reporting/readouts.
 */
export const useCampaignOutcomes = (campaignId?: number | string, options?: object) =>
  useLinkedReferenceObjects<CampaignOutcome>({
    campaignId,
    objectEndpoint: ENDPOINTS.outcomes,
    objectCacheKey: CacheKey.CampaignOutcome,
    linkEndpoint: ENDPOINTS.outcomeLinks,
    linkCacheKey: CacheKey.CampaignOutcomeLink,
    linkObjectField: 'outcome',
    options,
  });

/**
 * The campaign's question pool — the questions the AI may draw from, filtered server-side by campaign
 * and ordered by `order`. Each question optionally links to the outcome it explores (`outcome`) and
 * carries the customer/internal `context`. Admin sees hidden rows; consumers filter `hidden`.
 */
export const useCampaignQuestions = (campaignId?: number | string, options?: object) =>
  useLinkedReferenceObjects<CampaignQuestion>({
    campaignId,
    objectEndpoint: ENDPOINTS.questions,
    objectCacheKey: CacheKey.CampaignQuestion,
    linkEndpoint: ENDPOINTS.questionLinks,
    linkCacheKey: CacheKey.CampaignQuestionLink,
    linkObjectField: 'question',
    options,
  });

/**
 * The campaign's capability/play catalogue — the single de-duplicated source of truth, filtered
 * server-side by campaign and ordered by `order`. Content blocks, questions and scenarios reference a
 * capability by its `key`. Admin sees hidden rows; consumers filter `hidden`.
 */
export const useCampaignCapabilities = (campaignId?: number | string, options?: object) =>
  useLinkedReferenceObjects<CampaignCapability>({
    campaignId,
    objectEndpoint: ENDPOINTS.capabilities,
    objectCacheKey: CacheKey.CampaignCapability,
    linkEndpoint: ENDPOINTS.capabilityLinks,
    linkCacheKey: CacheKey.CampaignCapabilityLink,
    linkObjectField: 'capability',
    options,
  });

/**
 * The campaign's generic content blocks for one `category` (e.g. `guide_question`, `scenario`,
 * `agenda`, `ai_question_config`), filtered server-side by campaign + category and ordered by `order`.
 * The self-nesting hierarchy is fetched flat and assembled client-side via `parent` (see
 * `buildContentBlockTree`). This is the DB-authoritative replacement for the runner's bundled
 * `guided/config/*` lists; an empty result lets the runner fall back to its bundled constants so the
 * standalone demo still renders. Predefined question options/cues arrive in `payload` (non-AI).
 */
/**
 * The campaign's progression phases (see CampaignPhase). Server-ordered by `order`; hidden rows are
 * dropped so the runner view only sees live phases. Empty (un-seeded campaign) → the guided runner
 * falls back to its bundled `PHASE_ORDER`/`PHASE_LABEL`. Mirrors `useCustomerPersonas`.
 */
export const useCampaignPhases = (campaignId?: number | string, options?: object) => {
  const query = useFetchItems<CampaignPhase[]>(
    ENDPOINTS.phases,
    () => [CacheKey.CampaignPhase, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );
  return {
    ...query,
    phases: (query.data ?? []).filter((p) => !p.hidden),
  };
};

/**
 * The campaign's AI flow definitions (see CampaignPrompt). Server-ordered by `order`; hidden rows are
 * dropped so the runner view only sees live flows. Empty (un-seeded campaign) → the guided runner
 * falls back to its bundled prompt templates. Mirrors `useCampaignPhases`.
 */
export const useCampaignPrompts = (campaignId?: number | string, options?: object) => {
  const query = useFetchItems<CampaignPrompt[]>(
    ENDPOINTS.prompts,
    () => [CacheKey.CampaignPrompt, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );
  return {
    ...query,
    prompts: (query.data ?? []).filter((p) => !p.hidden),
  };
};

export const useCampaignContentBlocks = <P = CampaignContentBlock['payload']>(
  campaignId?: number | string,
  category?: string,
  options?: object
) =>
  useFetchItems<CampaignContentBlock<P>[]>(
    ENDPOINTS.contentBlocks,
    () => [CacheKey.CampaignContentBlock, campaignId ?? 'none', category ?? 'all'],
    campaignId ? { campaign: campaignId, ...(category ? { category } : {}) } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );

/**
 * The campaign's file assets (decks, PDFs, images) for one `kind`, filtered server-side by campaign +
 * kind and ordered by `order`. Each row is an S3 file reference (`object_key`); the runner presigns a
 * read URL to display/download. Empty → the runner falls back to its bundled deck paths.
 */
export const useCampaignAssets = (
  campaignId?: number | string,
  kind?: string,
  options?: object
) =>
  useFetchItems<CampaignAsset[]>(
    ENDPOINTS.assets,
    () => [CacheKey.CampaignAsset, campaignId ?? 'none', kind ?? 'all'],
    campaignId ? { campaign: campaignId, ...(kind ? { kind } : {}) } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );

/**
 * All `ai_question` rows — the stored-prompt catalogue the report builder binds report sections to.
 * ai_question is a flat global table (no campaign FK), so the same prompt can back a section in any
 * campaign; the builder just picks one to bind per `CampaignReportSection`.
 */
export const useFetchAiQuestions = (options?: object) =>
  useFetchItems<AIQuestion[]>('db/ai-question', CacheKey.AiQuestion, undefined, options);

/** A `UserGroup` row, trimmed to the fields the campaign builder's group pickers need. */
export interface UserGroupRef {
  id: number;
  name: string;
  description?: string | null;
}

/**
 * Every user group — the option source for the campaign builder's group-access pickers. `db/user-group`
 * is a small global table with no campaign scoping, so the whole list is pulled once and cached.
 */
export const useFetchUserGroups = (options?: object) =>
  useFetchItems<UserGroupRef[]>(
    ENDPOINTS.userGroups,
    () => [CacheKey.UserGroup, 'all'],
    undefined,
    { staleTime: 5 * 60 * 1000, ...(options || {}) }
  );

/**
 * The campaign's group-access assignments (see CampaignGroupAssignment), filtered server-side by
 * campaign. Each row links a user group to the campaign with an `access` role (`user` = who may
 * see/run it, `admin` = who administers it). The builder's dual group multi-select reconciles these
 * rows on save (create the added groups, delete the removed ones).
 */
export const useFetchCampaignGroupAssignments = (campaignId?: number | string, options?: object) =>
  useFetchItems<CampaignGroupAssignment[]>(
    ENDPOINTS.groupAssignments,
    () => [CacheKey.CampaignGroupAssignment, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId, ...(options || {}) }
  );

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

type MutationConfig = {
  endpoint: string;
  /** Keys to invalidate after any write, so both the resource and admin surfaces refresh. */
  invalidate: CacheKey[];
};

const MUTATIONS: Record<string, MutationConfig> = {
  campaign: {
    endpoint: ENDPOINTS.campaign,
    invalidate: [CacheKey.Campaigns, CacheKey.Campaign],
  },
  step: {
    endpoint: ENDPOINTS.step,
    invalidate: [CacheKey.CampaignStep],
  },
  enrollment: {
    endpoint: ENDPOINTS.enrollment,
    invalidate: [CacheKey.CampaignEnrollment],
  },
  delivery: {
    endpoint: ENDPOINTS.delivery,
    invalidate: [CacheKey.CampaignDelivery],
  },
  target: {
    endpoint: ENDPOINTS.target,
    invalidate: [CacheKey.CampaignDeliveryTarget],
  },
  stepStatus: {
    endpoint: ENDPOINTS.stepStatus,
    invalidate: [CacheKey.CampaignDeliveryStepStatus],
  },
  actionItem: {
    endpoint: ENDPOINTS.actionItem,
    invalidate: [CacheKey.CampaignActionItem],
  },
  workshopMeeting: {
    endpoint: ENDPOINTS.workshopMeeting,
    invalidate: [CacheKey.CampaignWorkshopMeeting],
  },
  reportSection: {
    endpoint: ENDPOINTS.reportSections,
    invalidate: [CacheKey.CampaignReportSection],
  },
  reportFigure: {
    endpoint: ENDPOINTS.reportFigures,
    invalidate: [CacheKey.CampaignReportFigure],
  },
  // The five reference objects are global (globalization 2026-09-25); their linked-subset views also
  // depend on the per-type link cache, so every object write invalidates that link key too.
  outcome: {
    endpoint: ENDPOINTS.outcomes,
    invalidate: [CacheKey.CampaignOutcome, CacheKey.CampaignOutcomeLink],
  },
  question: {
    endpoint: ENDPOINTS.questions,
    invalidate: [
      CacheKey.CampaignQuestion,
      CacheKey.CampaignQuestionLink,
      CacheKey.CampaignOutcome,
    ],
  },
  capability: {
    endpoint: ENDPOINTS.capabilities,
    invalidate: [CacheKey.CampaignCapability, CacheKey.CampaignCapabilityLink],
  },
  contentBlock: {
    endpoint: ENDPOINTS.contentBlocks,
    invalidate: [CacheKey.CampaignContentBlock],
  },
  asset: {
    endpoint: ENDPOINTS.assets,
    invalidate: [CacheKey.CampaignAsset],
  },
  phase: {
    endpoint: ENDPOINTS.phases,
    invalidate: [CacheKey.CampaignPhase],
  },
  prompt: {
    endpoint: ENDPOINTS.prompts,
    invalidate: [CacheKey.CampaignPrompt],
  },
  persona: {
    endpoint: ENDPOINTS.personas,
    invalidate: [CacheKey.CampaignCustomerPersona, CacheKey.CampaignCustomerPersonaLink],
  },
  teamRole: {
    endpoint: ENDPOINTS.teamRoles,
    invalidate: [CacheKey.CampaignWorkshopTeamRole, CacheKey.CampaignWorkshopTeamRoleLink],
  },
  // Membership link mutations. Creating/removing a link changes only which global objects a campaign
  // references, so each invalidates its link key AND the object key (the linked-subset view joins
  // both). Removing a link detaches the object from the campaign; it does NOT delete the global row.
  personaLink: {
    endpoint: ENDPOINTS.personaLinks,
    invalidate: [CacheKey.CampaignCustomerPersonaLink, CacheKey.CampaignCustomerPersona],
  },
  teamRoleLink: {
    endpoint: ENDPOINTS.teamRoleLinks,
    invalidate: [CacheKey.CampaignWorkshopTeamRoleLink, CacheKey.CampaignWorkshopTeamRole],
  },
  outcomeLink: {
    endpoint: ENDPOINTS.outcomeLinks,
    invalidate: [CacheKey.CampaignOutcomeLink, CacheKey.CampaignOutcome],
  },
  questionLink: {
    endpoint: ENDPOINTS.questionLinks,
    invalidate: [CacheKey.CampaignQuestionLink, CacheKey.CampaignQuestion],
  },
  capabilityLink: {
    endpoint: ENDPOINTS.capabilityLinks,
    invalidate: [CacheKey.CampaignCapabilityLink, CacheKey.CampaignCapability],
  },
  groupAssignment: {
    endpoint: ENDPOINTS.groupAssignments,
    invalidate: [CacheKey.CampaignGroupAssignment],
  },
};

// ---------------------------------------------------------------------------
// Enrollment create is idempotent on (campaign, user)
// ---------------------------------------------------------------------------
//
// `CampaignEnrollment` has a unique constraint on (user, campaign) (migration 0146). Several hooks
// (useSelfAttest, useQuestionAnswers, useCampaignJourneyMap, useCampaignStepStatus,
// useCampaignSalesforceLink) each independently resolve the SE's enrollment from
// `useFetchEnrollments` and, when they don't see one, POST a new one carrying their first write. Two
// such creates firing before the list refetches — or a single create against a momentarily stale
// list mid-invalidation — collide on that constraint and the server returns
// `400 {"non_field_errors": ["The fields must make a unique set."]}`, losing the write.
//
// This makes the enrollment create idempotent without touching any call site: concurrent creates for
// the same (campaign, user) are deduplicated to one POST, and if a create loses the race (or the
// list was stale), we look up the existing row and re-apply this payload's intent as a full-record
// PUT. A well-formed enrollment create has no other expected 400, so recovering on any failure where
// an enrollment already exists is safe.

/** In-flight enrollment creates, keyed `${campaign}:${user}`, so racing callers share one POST. */
const inFlightEnrollmentCreates = new Map<string, Promise<any>>();

const isPlainObject = (v: any): v is Record<string, any> =>
  v != null && typeof v === 'object' && !Array.isArray(v);

/** Deep-merge two plain objects; `b` wins on conflicts, nested plain objects merge recursively. */
const deepMerge = (a: any, b: any): any => {
  if (!isPlainObject(a) || !isPlainObject(b)) return b;
  const out: Record<string, any> = { ...a };
  for (const key of Object.keys(b)) {
    out[key] = isPlainObject(a[key]) && isPlainObject(b[key]) ? deepMerge(a[key], b[key]) : b[key];
  }
  return out;
};

/** The SE's existing enrollment for this campaign, or null. Scoped by campaign, matched by user. */
const findExistingEnrollment = async (
  endpoint: string,
  campaign: number,
  user: number
): Promise<any | null> => {
  const items = await fetchItems(endpoint, { campaign });
  const rows: any[] = Array.isArray(items) ? items : [];
  return rows.find((r) => r?.user === user) ?? null;
};

/** Layer this create payload's mutable fields onto an existing enrollment via a full-record PUT. */
const applyOntoEnrollment = (endpoint: string, existing: any, payload: Record<string, any>) =>
  putItem(endpoint, {
    ...existing,
    ...(payload.customer != null ? { customer: payload.customer } : {}),
    ...(payload.opportunity != null ? { opportunity: payload.opportunity } : {}),
    ...(payload.status ? { status: payload.status } : {}),
    metadata: deepMerge(existing.metadata || {}, payload.metadata || {}),
    id: existing.id,
  });

/** Get-or-create the SE's enrollment, tolerant of the unique (campaign, user) constraint. */
const ensureEnrollmentCreate = async (endpoint: string, payload: Record<string, any>) => {
  const key = `${payload.campaign}:${payload.user}`;
  const pending = inFlightEnrollmentCreates.get(key);
  if (pending) {
    // Another create is already in flight for this SE+campaign. Wait for it, then land this
    // payload's write onto the resulting row as a PUT rather than racing a second POST.
    const created = await pending.catch(() => null);
    const existing = created ?? (await findExistingEnrollment(endpoint, payload.campaign, payload.user));
    if (existing) return applyOntoEnrollment(endpoint, existing, payload);
  }

  const attempt = postItem(endpoint, payload);
  inFlightEnrollmentCreates.set(
    key,
    attempt.catch(() => null)
  );
  try {
    return await attempt;
  } catch (err) {
    const existing = await findExistingEnrollment(endpoint, payload.campaign, payload.user);
    if (existing) return applyOntoEnrollment(endpoint, existing, payload);
    throw err;
  } finally {
    inFlightEnrollmentCreates.delete(key);
  }
};

/**
 * Create / update / delete for one campaign table.
 *
 * `update` is a full-record PUT: pass the whole row spread with the change on top (`{ ...row, status
 * }`), never a delta, or the Go runtime 400s. Invalidation is by cache-key prefix so every list
 * keyed under that table refreshes regardless of which parent id it was scoped to.
 */
export const useCampaignMutations = (model: keyof typeof MUTATIONS) => {
  const queryClient = useQueryClient();
  const config = MUTATIONS[model];

  const onSettled = () => {
    config.invalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: [String(key)] }));
  };

  const create = useMutation({
    mutationFn: (payload: Record<string, any>) =>
      model === 'enrollment' && payload?.campaign != null && payload?.user != null
        ? ensureEnrollmentCreate(config.endpoint, payload)
        : postItem(config.endpoint, payload),
    onSettled,
  });

  const update = useMutation({
    mutationFn: (payload: Record<string, any> & { id: number }) => putItem(config.endpoint, payload),
    onSettled,
  });

  /**
   * Partial update that satisfies the full-record PUT contract for callers.
   *
   * The Go runtime treats a PUT as a full update and 400s on a delta missing a required field (and
   * 405s on PATCH), so every write must resend the whole record. This lets a caller express *intent*
   * as a delta: pass the current `base` row and the `delta` to apply, and it deep-merges them (so
   * nested `metadata` merges rather than clobbers) and PUTs the whole record. Prefer this over
   * hand-assembling `{ ...row, field }` at each call site.
   */
  const updatePartial = useMutation({
    mutationFn: ({
      base,
      delta,
    }: {
      base: Record<string, any> & { id: number };
      delta: Record<string, any>;
    }) => putItem(config.endpoint, { ...deepMerge(base, delta), id: base.id }),
    onSettled,
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteItem(`${config.endpoint}/${id}`),
    onSettled,
  });

  return {
    create,
    update,
    updatePartial,
    remove,
    isSaving:
      create.isPending || update.isPending || updatePartial.isPending || remove.isPending,
    error: create.error || update.error || updatePartial.error || remove.error,
  };
};

// ---------------------------------------------------------------------------
// Reference-object admin (globalization, 2026-09-25)
// ---------------------------------------------------------------------------

/** The five globalized reference-object types and the wiring each needs (object + link surfaces). */
type ReferenceObjectType = 'persona' | 'teamRole' | 'outcome' | 'question' | 'capability';

const REFERENCE_OBJECT_CONFIG: Record<
  ReferenceObjectType,
  {
    objectMut: keyof typeof MUTATIONS;
    linkMut: keyof typeof MUTATIONS;
    /** The link row's FK field pointing at the object. */
    linkField: string;
    objectEndpoint: string;
    objectCacheKey: CacheKey;
    linkEndpoint: string;
    linkCacheKey: CacheKey;
  }
> = {
  persona: {
    objectMut: 'persona',
    linkMut: 'personaLink',
    linkField: 'persona',
    objectEndpoint: ENDPOINTS.personas,
    objectCacheKey: CacheKey.CampaignCustomerPersona,
    linkEndpoint: ENDPOINTS.personaLinks,
    linkCacheKey: CacheKey.CampaignCustomerPersonaLink,
  },
  teamRole: {
    objectMut: 'teamRole',
    linkMut: 'teamRoleLink',
    linkField: 'role',
    objectEndpoint: ENDPOINTS.teamRoles,
    objectCacheKey: CacheKey.CampaignWorkshopTeamRole,
    linkEndpoint: ENDPOINTS.teamRoleLinks,
    linkCacheKey: CacheKey.CampaignWorkshopTeamRoleLink,
  },
  outcome: {
    objectMut: 'outcome',
    linkMut: 'outcomeLink',
    linkField: 'outcome',
    objectEndpoint: ENDPOINTS.outcomes,
    objectCacheKey: CacheKey.CampaignOutcome,
    linkEndpoint: ENDPOINTS.outcomeLinks,
    linkCacheKey: CacheKey.CampaignOutcomeLink,
  },
  question: {
    objectMut: 'question',
    linkMut: 'questionLink',
    linkField: 'question',
    objectEndpoint: ENDPOINTS.questions,
    objectCacheKey: CacheKey.CampaignQuestion,
    linkEndpoint: ENDPOINTS.questionLinks,
    linkCacheKey: CacheKey.CampaignQuestionLink,
  },
  capability: {
    objectMut: 'capability',
    linkMut: 'capabilityLink',
    linkField: 'capability',
    objectEndpoint: ENDPOINTS.capabilities,
    objectCacheKey: CacheKey.CampaignCapability,
    linkEndpoint: ENDPOINTS.capabilityLinks,
    linkCacheKey: CacheKey.CampaignCapabilityLink,
  },
};

/**
 * Admin orchestration for one globalized reference-object type in a campaign builder. The five types
 * (persona/team role/outcome/question/capability) are global rows a campaign references through a
 * per-type link table. This hook hides that two-table shape behind the operations an editor needs:
 *
 * - `linked`   — the objects THIS campaign references (raw, hidden included), sorted by `order`.
 * - `unlinked` — global objects NOT yet referenced here, for the "attach existing" picker.
 * - `createLinked(fields)` — create a NEW global object then link it to this campaign (one flow).
 * - `attachExisting(objectId)` — link an already-existing global object to this campaign.
 * - `detach(objectId)` — remove this campaign's link (does NOT delete the global object; another
 *   campaign may still reference it).
 * - `update` / `updatePartial` — edit the global object in place (full-record PUT, as elsewhere).
 *
 * `createLinked` resolves once both the object POST and the link POST land, so callers can await it.
 */
export const useReferenceObjectAdmin = <
  TObj extends { id: number; order?: number; hidden?: boolean },
>(
  type: ReferenceObjectType,
  campaignId?: number,
) => {
  const cfg = REFERENCE_OBJECT_CONFIG[type];
  const objectMut = useCampaignMutations(cfg.objectMut);
  const linkMut = useCampaignMutations(cfg.linkMut);

  // Global (campaign-free) mode powers the shared reference-object library shown on
  // /admin/campaigns/references with no campaign selected: list EVERY global object and manage the
  // library itself (create/edit/delete), rather than a single campaign's linked subset.
  const global = campaignId == null;

  const linksQuery = useFetchItems<Array<Record<string, number> & { id: number }>>(
    cfg.linkEndpoint,
    () => [cfg.linkCacheKey, campaignId ?? 'none'],
    campaignId ? { campaign: campaignId } : undefined,
    { enabled: !!campaignId },
  );
  // Every link row for this type (no campaign filter), loaded only in global mode so the library can
  // show each object's "used by N campaigns". Kept separate from `linksQuery` so campaign-scoped
  // `linked`/`unlinked` stay exactly as before.
  const allLinksQuery = useFetchItems<Array<Record<string, number> & { id: number }>>(
    cfg.linkEndpoint,
    () => [cfg.linkCacheKey, 'all'],
    undefined,
    { enabled: global },
  );
  const objectsQuery = useFetchItems<TObj[]>(
    cfg.objectEndpoint,
    () => [cfg.objectCacheKey, 'all'],
    undefined,
    { enabled: true },
  );

  const links = linksQuery.data ?? [];
  const linkByObjectId = new Map(links.map((l) => [l[cfg.linkField], l]));
  const linkedIds = new Set(links.map((l) => l[cfg.linkField]));
  const objects = objectsQuery.data ?? [];
  const byOrder = (a: TObj, b: TObj) => (a.order ?? 0) - (b.order ?? 0);
  const linked = objects.filter((o) => linkedIds.has(o.id)).sort(byOrder);
  const unlinked = objects.filter((o) => !linkedIds.has(o.id)).sort(byOrder);
  const all = [...objects].sort(byOrder);

  // objectId -> number of distinct campaigns referencing it (global mode only; empty otherwise).
  const campaignsByObject = new Map<number, Set<number>>();
  (allLinksQuery.data ?? []).forEach((l) => {
    const objectId = l[cfg.linkField];
    const campaign = l.campaign;
    if (!campaignsByObject.has(objectId)) campaignsByObject.set(objectId, new Set());
    campaignsByObject.get(objectId)!.add(campaign);
  });
  const usageCount = (objectId: number) => campaignsByObject.get(objectId)?.size ?? 0;

  /** Create a new global object, then link it to this campaign. Resolves with the created object. */
  const createLinked = (fields: Record<string, any>) =>
    new Promise<TObj>((resolve, reject) => {
      if (!campaignId) {
        reject(new Error('createLinked requires a campaign id'));
        return;
      }
      objectMut.create.mutate(fields, {
        onSuccess: (obj: any) =>
          linkMut.create.mutate(
            { campaign: campaignId, [cfg.linkField]: obj.id },
            { onSuccess: () => resolve(obj as TObj), onError: reject },
          ),
        onError: reject,
      });
    });

  /** Link an already-existing global object to this campaign (attach without creating). */
  const attachExisting = (objectId: number) => {
    if (!campaignId) return;
    linkMut.create.mutate({ campaign: campaignId, [cfg.linkField]: objectId });
  };

  /** Remove this campaign's reference to the object. Leaves the global object intact. */
  const detach = (objectId: number) => {
    const link = linkByObjectId.get(objectId);
    if (link) linkMut.remove.mutate(link.id);
  };

  /** Create a new global object with NO campaign link (library/global mode). Resolves with the object. */
  const create = (fields: Record<string, any>) =>
    new Promise<TObj>((resolve, reject) => {
      objectMut.create.mutate(fields, {
        onSuccess: (obj: any) => resolve(obj as TObj),
        onError: reject,
      });
    });

  /**
   * Delete the global object itself (library/global mode). This removes it everywhere — any campaign
   * links to it are expected to cascade server-side. Unlike `detach`, there is no campaign scope.
   */
  const removeGlobal = (objectId: number) => objectMut.remove.mutate(objectId);

  return {
    linked,
    unlinked,
    all,
    links,
    usageCount,
    isLoading:
      linksQuery.isLoading || objectsQuery.isLoading || (global && allLinksQuery.isLoading),
    isSaving: objectMut.isSaving || linkMut.isSaving,
    error: objectMut.error || linkMut.error,
    update: objectMut.update,
    updatePartial: objectMut.updatePartial,
    createLinked,
    attachExisting,
    detach,
    create,
    removeGlobal,
  };
};

/**
 * Persist a new tree order/nesting for campaign steps.
 *
 * Takes one or more *sibling groups* — each `{ parentId, ordered }` where `ordered` is the rows in
 * their new visual order under that parent — and PUTs `{ ...row, parent, order }` for every row
 * whose `order` or `parent` actually changed, then invalidates the step list once. This covers both
 * plain reordering within a level and re-parenting (drag across groups): pass the source group
 * (compacted) and the destination group (with the moved node spliced in) together, and the moved
 * node's PUT carries its new `parent` while both groups get their `order` compacted in one batch.
 *
 * Each write carries the whole row with the new `parent`/`order` on top: the generic runtime treats
 * a PUT as a full update and 400s on a delta missing a required field (a step's `name`), so a bare
 * `{ id, order }` is rejected. No-op rows (already at the right parent+order) are skipped.
 */
export const useReorderSteps = () => {
  const queryClient = useQueryClient();
  const config = MUTATIONS.step;

  return useMutation({
    mutationFn: async (groups: Array<{ parentId: number | null; ordered: CampaignStep[] }>) => {
      const writes: Array<Promise<unknown>> = [];
      groups.forEach(({ parentId, ordered }) => {
        ordered.forEach((row, index) => {
          const sameParent = (row.parent ?? null) === (parentId ?? null);
          if (row.order === index && sameParent) return;
          writes.push(putItem(config.endpoint, { ...row, parent: parentId, order: index }));
        });
      });
      await Promise.all(writes);
    },
    onSettled: () =>
      config.invalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: [String(key)] })),
  });
};

/** The acting user (for the queue worker to send *as* them when they've connected Webex). */
const actingUserOptions = (): Record<string, any> => {
  const { id, email } = store.getState().user.userData;
  const opts: Record<string, any> = {};
  if (id != null) opts.acting_user_id = id;
  if (email) opts.acting_user_email = email;
  return opts;
};

export interface EnablementReminderInput {
  /** Who receives the card — the row's own person (a manager or an IC). */
  recipientEmail: string;
  /** Plain-text fallback for clients that can't render the Adaptive Card. */
  messageText: string;
  /** The Adaptive Card body (forwarded verbatim to Webex as an attachment). */
  card: AdaptiveCard;
}

/**
 * Submit a single `webex_notification` order that sends an enablement-completion reminder card to one
 * person — modeled on the workshop-prep notify order. Returns null (submits nothing) with no recipient.
 *
 * Reminders are normally sent *as* the acting user (their Webex OAuth token) so a report sees it from
 * their manager. The exception is a self-reminder (recipient == acting user): Webex can't create a 1:1
 * room with yourself, so we OMIT the acting-user options for that case — the worker then falls back to
 * the bot token (WEBEX_BOT_TOKEN) and the DM is delivered from the Toolbox bot instead of 400ing.
 */
export const useSubmitEnablementReminder = () =>
  useMutation<Order | null, Error, EnablementReminderInput>({
    mutationFn: async ({ recipientEmail, messageText, card }) => {
      if (!recipientEmail) return null;
      const acting = actingUserOptions();
      const isSelf =
        !!acting.acting_user_email &&
        recipientEmail.trim().toLowerCase() === String(acting.acting_user_email).trim().toLowerCase();
      const body = {
        order_key: 'campaign_enablement_reminder',
        options: { source: 'campaign_leader' },
        tasks: [
          {
            task_key: 'notify',
            task_type: 'webex_notification',
            options: {
              recipient_email: recipientEmail,
              message_text: messageText,
              card_content: card,
              // Self-reminder → send from the bot (drop acting-user) so it doesn't self-send-400.
              ...(isSelf ? {} : acting),
            },
          },
        ],
        submit_to_queue: true,
      };
      return postItem('queue/orders', body);
    },
  });

/**
 * Poll one enablement-reminder order until it reaches a terminal state, so the Notify button can reflect
 * the true outcome rather than the premature "enqueued" state. Enabled only while an order id is set and
 * the order hasn't finished; stops polling once terminal. Mirrors {@link useMeetingOrderStatus}.
 */
export const useEnablementReminderOrderStatus = (orderId: string | null | undefined) =>
  useQueueOrderStatus(orderId, [CacheKey.CampaignEnablementReminderOrder, orderId ?? 'none']);

/** What actually happened to a reminder once its order finished. */
export type ReminderOutcome = 'sent' | 'skipped' | 'failed';

/** The Webex-notification task on a reminder order (there is exactly one). */
const reminderTask = (order: Order | undefined) =>
  order?.tasks?.find((t) => t.task_type === 'webex_notification');

/**
 * The Webex send returned an error the worker MASKED as task success. `send_notification` wraps
 * `send_direct_message`'s return dict and reports `success: True` for any dict — including the error
 * dict Webex failures produce (`{status:'error', status_code, message}`) — so a green task can still
 * carry a 400. We detect that embedded error (status/error/status_code) so it reads as a real failure,
 * not a benign skip. (A skip is `{status:'skipped'}` — no error, no message_id.)
 */
const embeddedSendError = (order: Order | undefined): Record<string, any> | null => {
  const result = reminderTask(order)?.result as Record<string, any> | undefined;
  const inner = result?.result as Record<string, any> | undefined;
  if (!inner) return null;
  const failed =
    inner.status === 'error' || !!inner.error || (typeof inner.status_code === 'number' && inner.status_code >= 400);
  return failed ? inner : null;
};

/**
 * Classify a finished reminder order into a user-facing outcome — null while still in flight.
 *
 * A green order does NOT prove a message went out: the worker reports both a *skip* (no Webex token, or
 * a non-prod recipient outside the allow-list) AND a *masked send error* as task success. So we rank:
 * a failed/partial order or failed task → `failed`; a completed task with an embedded Webex error (a
 * 400, self-send, etc.) → `failed`; a completed task carrying a `message_id` → `sent`; anything else
 * that completed delivered nothing → `skipped`.
 */
export const classifyReminderOrder = (order: Order | undefined): ReminderOutcome | null => {
  if (!order || !isTerminalQueueOrder(order.status)) return null;
  const task = reminderTask(order);
  if (order.status !== 'completed' || task?.status === 'failed') return 'failed';
  if (embeddedSendError(order)) return 'failed';
  const result = task?.result as Record<string, any> | undefined;
  return result?.message_id ? 'sent' : 'skipped';
};

/**
 * A human-readable reason a finished reminder failed, for a tooltip/banner — the worker's task
 * `error_message`, else the masked Webex error message, else the order's own error. Null when it didn't
 * fail. Self-send (notifying yourself) surfaces here as Webex's "Failed to create room." 400.
 */
export const reminderErrorFromOrder = (order: Order | undefined): string | null => {
  if (classifyReminderOrder(order) !== 'failed') return null;
  const task = reminderTask(order);
  return (
    task?.error_message ||
    embeddedSendError(order)?.message ||
    order?.error_message ||
    'The Webex message could not be sent.'
  );
};
